import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { clsx } from 'clsx';
import { appUrl } from '@/lib/hosts';
import { ThemeToggle } from '@/components/ThemeToggle';
import { NeobrutalButton } from './NeobrutalButton';

const NAV_LINKS: ReadonlyArray<{ to: string; key: string }> = [
  { to: '/fonctionnalites', key: 'nav.features' },
  { to: '/tarifs', key: 'nav.pricing' },
  { to: '/demo', key: 'nav.demo' },
  { to: '/blog', key: 'nav.blog' },
  { to: '/a-propos', key: 'nav.about' },
  { to: '/contact', key: 'nav.contact' },
];

export function NavMarketingV2() {
  const { t } = useTranslation('marketing');
  const { isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <div className="px-4 pt-3 sm:px-6 md:px-8">
      <nav
        aria-label="Navigation principale"
        className="sticky top-3 z-50 mx-auto flex max-w-[1216px] items-center justify-between border-2 border-charcoal-800 bg-white px-5 py-3 shadow-neobrutal-6 md:px-6"
      >
        <Link to="/" className="flex items-center gap-2.5" aria-label="Velko — accueil">
          <img src="/brand/velko-lockup.svg" alt="Velko" className="h-7 w-auto md:h-8" />
        </Link>

        <ul className="hidden items-center gap-7 md:flex">
          {NAV_LINKS.map((link) => (
            <li key={link.to}>
              <NavLink
                to={link.to}
                className={({ isActive }) =>
                  clsx(
                    'text-sm font-medium font-marketing-body text-charcoal-700 transition-colors hover:text-charcoal-900',
                    isActive && 'text-charcoal-900 underline-lime decoration-0',
                  )
                }
              >
                {t(link.key)}
              </NavLink>
            </li>
          ))}
        </ul>

        <div className="hidden items-center gap-3 md:flex">
          <ThemeToggle
            variant="icon"
            size="sm"
            className="border-2 border-charcoal-800 bg-white text-charcoal-800 shadow-neobrutal-2 transition-transform hover:-translate-x-[1px] hover:-translate-y-[1px] hover:shadow-neobrutal-4 dark:border-charcoal-700 dark:bg-charcoal-800 dark:text-white"
          />
          {isAuthenticated ? (
            <NeobrutalButton as="a" href={appUrl('/dashboard')} variant="primary" size="sm">
              {t('nav.dashboard')}
            </NeobrutalButton>
          ) : (
            <>
              <Link
                to="/login"
                className="font-marketing-body text-sm font-medium text-charcoal-700 hover:text-charcoal-900"
              >
                {t('nav.login')}
              </Link>
              <NeobrutalButton as="link" to="/register" variant="primary" size="sm">
                {t('nav.register')}
              </NeobrutalButton>
            </>
          )}
        </div>

        <button
          type="button"
          aria-label={open ? 'Fermer le menu' : 'Ouvrir le menu'}
          aria-expanded={open}
          onClick={() => setOpen((v) => !v)}
          className="flex h-10 w-10 items-center justify-center border-2 border-charcoal-800 bg-white shadow-neobrutal-2 transition-transform hover:-translate-x-[1px] hover:-translate-y-[1px] hover:shadow-neobrutal-4 md:hidden"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            stroke="currentColor"
            strokeWidth="2.5"
            fill="none"
          >
            {open ? (
              <>
                <path d="M4 4l12 12" />
                <path d="M16 4L4 16" />
              </>
            ) : (
              <>
                <path d="M3 6h14" />
                <path d="M3 10h14" />
                <path d="M3 14h14" />
              </>
            )}
          </svg>
        </button>
      </nav>

      {/* Mobile menu panel */}
      {open && (
        <div className="fixed inset-0 top-20 z-40 md:hidden" role="dialog" aria-modal="true">
          <div
            className="absolute inset-0 bg-charcoal-800/40"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="relative mx-4 border-2 border-charcoal-800 bg-white shadow-neobrutal-8">
            <ul className="divide-y-2 divide-charcoal-800">
              {NAV_LINKS.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="block px-5 py-4 font-marketing-display text-lg font-semibold text-charcoal-800 hover:bg-paper"
                  >
                    {t(link.key)}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="flex flex-col gap-3 border-t-2 border-charcoal-800 p-5">
              <div className="flex items-center justify-between pb-1">
                <span className="font-marketing-body text-xs font-semibold uppercase tracking-wider text-charcoal-600 dark:text-charcoal-400">
                  Thème
                </span>
                <ThemeToggle size="sm" />
              </div>
              {isAuthenticated ? (
                <NeobrutalButton as="a" href={appUrl('/dashboard')} variant="primary" size="md">
                  {t('nav.dashboard')}
                </NeobrutalButton>
              ) : (
                <>
                  <NeobrutalButton as="link" to="/login" variant="secondary" size="md">
                    {t('nav.login')}
                  </NeobrutalButton>
                  <NeobrutalButton as="link" to="/register" variant="primary" size="md">
                    {t('nav.register')}
                  </NeobrutalButton>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
